/**
 * Звонки поверх приложения (изменение v1.14).
 *
 * Монтируется один раз в корне маршрутов, вне стека экранов: переходы не пересоздают сессию, а
 * входящий вызов виден на любом экране. Сам хост — только условие: после входа, при включённых
 * звонках или пока идёт звонок.
 *
 * Слой вызова (экраны, менеджер) — в стартовом бандле, а не отдельным чанком: сбой загрузки чанка
 * Chromium запоминает до перезагрузки страницы, и входящий вызов в видимой вкладке пропал бы
 * молча. Лениво грузится только сессия WebRTC с криптографией — при звонке, с объяснением сбоя.
 */
import { Component, type ReactNode } from 'react'

import { useCallStore } from '@features/calls/store'

import { useAppStore } from '../../model/app-store'
import { CallLayer } from './CallLayer'

/** Сбой внутри слоя звонков не роняет приложение: слой гаснет, остальное работает. */
class CallLayerBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  override componentDidCatch(error: unknown): void {
    console.error('звонки: сбой слоя звонков', error)
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children
  }
}

export function CallHost() {
  const phase = useAppStore((s) => s.phase)
  const deviceId = useAppStore((s) => s.me?.user.device_id ?? null)
  const enabled = useAppStore((s) => s.config?.features?.['calls'] === true)
  // Выключение звонков запрещает новые, а идущий доживает — как и на сервере.
  const live = useCallStore((s) => s.view !== null)
  if (phase !== 'ready' || (!enabled && !live)) return null
  return (
    <CallLayerBoundary>
      <CallLayer deviceId={deviceId} />
    </CallLayerBoundary>
  )
}
